import { createRoot } from "react-dom/client";

import type { ImageModal } from "../components/image/ImageModal";

import { isMobile } from "./isMobile";
import { parseDecimal } from "./parseDecimal";

import { linkCardClass } from "~/client/components/richTextEditor/plugins/link/constants";

const MAX_ENTRY_WIDTH = 748;
const PAPER_RATIO = Math.sqrt(2);

function isPdfSupported() {
  return !isMobile;
}

const insertAfter = (element: Element, insertTo: Element) => {
  insertTo.parentNode?.insertBefore(element, insertTo.nextSibling);
};

async function processImageLink(content: Element) {
  if (content.querySelector(".imageModalPortal")) {
    return;
  }

  const imgs = content.querySelectorAll<HTMLImageElement>("img:not(.emoji)");

  if (!imgs.length) {
    return;
  }

  const loadingTasks: Array<() => void> = [];
  const loadImagesImmediately = () => {
    loadingTasks.forEach((task) => task());
    loadingTasks.length = 0;
  };

  const portal = content.appendChild(document.createElement("div"));
  portal.classList.add("imageModalPortal");
  portal.style.display = "hidden";

  const { ImageModal } = await import("../components/image/ImageModal");

  let imageModal: ImageModal;
  const root = createRoot(portal);
  const imageModalElement = (
    <ImageModal
      ref={(component) => {
        imageModal = component!;
      }}
    />
  );
  root.render(imageModalElement);

  // `PreviewBox` コンポーネント内の内容が更新されたとき、`ImageModal` コンポーネントが複数マウントされないようにするために都度アンマウントする
  portal.addEventListener("updateMarkdownContent", () => {
    // 以下の警告を避けるため、 `setTimeout()` を使用して次のイベントサイクルでアンマウントする
    // Warning: Attempted to synchronously unmount a root while React was already rendering.
    // React cannot finish unmounting the root until the current render has completed, which may lead to a race condition.
    setTimeout(() => {
      root.unmount();
      portal.remove();
    });
  });

  imgs.forEach((img): void => {
    if (img.parentElement?.tagName === "A" || img.closest(".modal")) {
      return;
    }

    const handler = (event) => {
      event.preventDefault();
      event.stopPropagation();

      if (!imageModal) {
        return;
      }

      // data-src and data-srcSet are provided for lazy loaders
      // data-src-original and data-srcSet are provided as the original image

      const metadata = JSON.parse(img.dataset.meta || "{}");
      const src = img.dataset.srcOriginal || img.dataset.src || img.src;
      const srcSet = img.getAttribute("data-srcSet-original") || img.getAttribute("data-srcSet") || img.srcset;
      const width = metadata.width;
      const height = metadata.height;

      imageModal.setState({
        src,
        srcSet,
        width,
        height,
        isOpen: true,
      });
    };

    img.classList.add("modalImage");
    img.removeEventListener("click", handler);
    img.addEventListener("click", handler);

    if (img.dataset.lazy) {
      img.classList.remove("loaded");
      img.removeAttribute("data-loaded");
      img.classList.add("lazy");
      import("lozad").then(({ default: lozad }) => {
        const observer = lozad(img, {
          loaded() {
            img.classList.add("loaded");
          },
        });
        observer.observe();

        loadingTasks.push(() => {
          observer.triggerLoad(img);
        });
      });
    }
  });

  const printMedia = matchMedia("print");
  if (printMedia.matches) {
    loadImagesImmediately();
  } else {
    printMedia.addEventListener("change", (event) => {
      if (event.matches) {
        loadImagesImmediately();
      }
    });
  }
}

function safeJsonParse(s: string | undefined) {
  try {
    return JSON.parse(s!);
  } catch {
    return {};
  }
}

async function insertIfPlayable(media: HTMLMediaElement, a: HTMLAnchorElement) {
  const originalDisplay = media.style.display;
  media.style.display = "none";

  insertAfter(media, a);

  media.addEventListener("canplay", () => {
    media.style.display = originalDisplay;
    a.classList.add("is-media-previewed");
  });
  media.addEventListener("error", () => {
    media.remove();
  });
}

function processVideoPreview(a: HTMLAnchorElement, mimeType: string) {
  const media = document.createElement("video");
  media.classList.add("is-media-preview");
  media.controls = true;
  media.preload = "metadata";
  media.src = a.href;

  if (!media.canPlayType(mimeType)) {
    return;
  }
  insertIfPlayable(media, a);
}

function processAudioPreview(a: HTMLAnchorElement, mimeType: string) {
  const media = document.createElement("audio");
  media.classList.add("is-media-preview");
  media.controls = true;
  media.preload = "metadata";
  media.src = a.href;

  if (!media.canPlayType(mimeType)) {
    return;
  }
  insertIfPlayable(media, a);
}

// This must be processed in the client side,
// because most of smartphone browsers does not support PDF,
// and it will be replaced by PDF.js in a future.
function processPdfPreview(a: HTMLAnchorElement, metadata: { width?: string; height?: string }) {
  const width = metadata.width ? Math.min(parseDecimal(metadata.width), MAX_ENTRY_WIDTH) : MAX_ENTRY_WIDTH;
  const maxHeight = Math.round(width * PAPER_RATIO);
  const height = metadata.height ? Math.min(parseDecimal(metadata.height), maxHeight) : maxHeight;

  const aspectRatioPercent = Math.round((height / width) * 100);

  const iframe = document.createElement("iframe");
  iframe.src = a.href + "#navpanes=0";
  iframe.width = `${width}`;
  iframe.height = `${height}`;

  const element = document.createElement("div");
  element.classList.add("iframeWrapper", "is-media-preview");
  element.style.paddingBottom = `${aspectRatioPercent}%`;
  element.appendChild(iframe);

  a.classList.add("is-media-previewed");
  insertAfter(element, a);
}

function isSingleLinkInParagraph(a: HTMLAnchorElement) {
  if (!(a.getAttribute("href") && a.textContent)) {
    return false;
  }

  const parent = a.parentElement!;

  if (parent.tagName !== "P") {
    return false;
  }

  // Link in an independent paragraph
  if (parent.childNodes.length === 1) {
    return true;
  }

  // Link in an paragraph with text contents,
  // but it is separated by new lines
  const previous = a.previousSibling;
  const next = a.nextSibling;
  if (previous && previous.textContent !== "\n") {
    return false;
  }
  if (next && !(next.nodeType === Node.ELEMENT_NODE && (next as HTMLElement).tagName === "BR")) {
    return false;
  }
  return true;
}

export function addMediaPreviewToAnchor(a: HTMLAnchorElement) {
  if (a.dataset.kibelaMediaPreview) {
    return;
  }
  a.dataset.kibelaMediaPreview = "true";

  const mimeType = a.getAttribute("type");
  if (!mimeType) {
    return;
  }

  if (mimeType === "application/pdf") {
    const metadata = safeJsonParse(a.dataset.meta);
    const href = a.getAttribute("href") || "";
    const textContent = a.textContent || "";

    if ((href.endsWith(".pdf") || textContent.endsWith(".pdf")) && isPdfSupported()) {
      processPdfPreview(a, metadata);
    }
  } else if (mimeType.startsWith("video/")) {
    processVideoPreview(a, mimeType);
  } else if (mimeType.startsWith("audio/")) {
    processAudioPreview(a, mimeType);
  }
}

export const fallBackLinkCardImages = (content: Element) => {
  const images = content.querySelectorAll(`a.${linkCardClass} span > img`);
  images.forEach((img) => {
    img.setAttribute("alt", "");
  });
};

export function processMediaPreview(content: Element) {
  for (const a of content.querySelectorAll<HTMLAnchorElement>("a[type][href]")) {
    if (!isSingleLinkInParagraph(a)) {
      return;
    }
    addMediaPreviewToAnchor(a);
  }
}

function processMarkdownImageLink(content: Element) {
  const imgs = content.querySelectorAll<HTMLImageElement>("img:not(.emoji)");
  imgs.forEach((img) => {
    if (img.parentElement?.tagName === "A") {
      const src = img.dataset.srcOriginal || img.dataset.src || img.src;
      img.src = src;
    }
  });
}

export async function processMarkdown(content: Element) {
  await processImageLink(content);
  processMarkdownImageLink(content);
  processMediaPreview(content);
  fallBackLinkCardImages(content);
}
